import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useCreateEmployee } from '../../contexts/CreateEmployeeContext'
import {
  Search,
  Users,
  FileText,
  Shield,
  Settings,
  Plus,
  Mail,
  BarChart3,
  FolderOpen,
  LayoutTemplate,
  CheckSquare,
  TrendingUp,
  ArrowRight,
  X,
} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { globalSearch, type GlobalSearchResult } from '../../lib/dashboard'
import { useProfile } from '../../contexts/ProfileContext'
import { trackEvent } from '../../lib/monitoring'

// ─── Types ────────────────────────────────────────────────────────────────────

interface CommandMenuProps {
  isOpen: boolean
  onClose: () => void
  onEmployeeClick?: (employeeId: string) => void
  onAddEmployee?: () => void
}

interface Command {
  id: string
  title: string
  subtitle: string
  icon: React.ComponentType<{ className?: string }>
  action: () => void
  category: 'navigation' | 'actions' | 'search'
  keywords: string[]
}

type ListItem = {
  id: string
  title: string
  subtitle: string
  icon: React.ComponentType<{ className?: string }>
  action: () => void
  category: 'navigation' | 'actions' | 'search'
  originalIndex: number
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const CATEGORY_LABELS: Record<string, string> = {
  search: 'Search Results',
  navigation: 'Go to',
  actions: 'Quick Actions',
}

function getResultIcon(type: string): React.ComponentType<{ className?: string }> {
  switch (type) {
    case 'employee': return Users
    case 'task': return CheckSquare
    case 'template': return LayoutTemplate
    case 'document': return FolderOpen
    default: return FileText
  }
}

// ─── CommandMenu ──────────────────────────────────────────────────────────────

export default function CommandMenu({
  isOpen,
  onClose,
  onEmployeeClick,
  onAddEmployee,
}: CommandMenuProps) {
  const { openCreateEmployee } = useCreateEmployee()
  const { profile } = useProfile()
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [searchResults, setSearchResults] = useState<GlobalSearchResult[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const handleNavigate = useCallback((path: string) => {
    navigate(path)
    onClose()
  }, [navigate, onClose])

  const commands: Command[] = [
    {
      id: 'nav-dashboard',
      title: 'Overview',
      subtitle: 'Dashboard and KPIs',
      icon: BarChart3,
      action: () => handleNavigate('/dashboard'),
      category: 'navigation',
      keywords: ['dashboard', 'home', 'overview', 'kpi', 'progress'],
    },
    {
      id: 'nav-people',
      title: 'People',
      subtitle: 'Workforce overview',
      icon: Users,
      action: () => handleNavigate('/people'),
      category: 'navigation',
      keywords: ['employees', 'team', 'staff', 'people', 'workforce'],
    },
    {
      id: 'nav-blueprints',
      title: 'Blueprints',
      subtitle: 'Onboarding templates and phases',
      icon: LayoutTemplate,
      action: () => handleNavigate('/blueprints'),
      category: 'navigation',
      keywords: ['blueprints', 'journey', 'templates', 'phases', 'milestones', 'onboarding', 'workflows'],
    },
    {
      id: 'nav-my-work',
      title: 'My Work',
      subtitle: 'Your assigned onboarding tasks',
      icon: CheckSquare,
      action: () => handleNavigate('/my-work'),
      category: 'navigation',
      keywords: ['my work', 'tasks', 'todos', 'work', 'checklist', 'assigned'],
    },
    {
      id: 'nav-operations',
      title: 'AI Operations',
      subtitle: 'Ask about readiness, risk, schedules, or drafts',
      icon: FileText,
      action: () => handleNavigate('/operations'),
      category: 'navigation',
      keywords: ['ai', 'operations', 'readiness', 'risk', 'schedules', 'drafts'],
    },
    {
      id: 'nav-compliance',
      title: 'Compliance',
      subtitle: 'Compliance health dashboard',
      icon: Shield,
      action: () => handleNavigate('/compliance'),
      category: 'navigation',
      keywords: ['compliance', 'policies', 'regulations', 'health'],
    },
    {
      id: 'nav-documents',
      title: 'Documents',
      subtitle: 'Manage employee documents',
      icon: FolderOpen,
      action: () => handleNavigate('/admin/documents'),
      category: 'navigation',
      keywords: ['documents', 'files', 'uploads', 'storage'],
    },
    {
      id: 'nav-reports',
      title: 'Reports',
      subtitle: 'Analytics and insights',
      icon: TrendingUp,
      action: () => handleNavigate('/reports'),
      category: 'navigation',
      keywords: ['reports', 'analytics', 'insights', 'trends'],
    },
    {
      id: 'nav-settings',
      title: 'Settings',
      subtitle: 'Company, Email, Billing, Team',
      icon: Settings,
      action: () => handleNavigate('/settings'),
      category: 'navigation',
      keywords: ['settings', 'preferences', 'configuration', 'account', 'company', 'billing', 'email', 'team'],
    },
    {
      id: 'nav-billing',
      title: 'Billing',
      subtitle: 'Subscription and payment',
      icon: Settings,
      action: () => handleNavigate('/settings?tab=billing'),
      category: 'navigation',
      keywords: ['billing', 'payment', 'subscription', 'plan', 'invoice'],
    },
    {
      id: 'action-add-employee',
      title: 'Add Person',
      subtitle: 'Create a new employee and optionally send invite',
      icon: Plus,
      action: () => {
        if (onAddEmployee) onAddEmployee()
        else openCreateEmployee()
        onClose()
      },
      category: 'actions',
      keywords: ['add', 'create', 'new', 'employee', 'hire', 'invite', 'person', 'onboarding'],
    },
    {
      id: 'action-send-invite',
      title: 'Send Invite',
      subtitle: 'Go to People to invite employees',
      icon: Mail,
      action: () => {
        handleNavigate('/people')
        onClose()
      },
      category: 'actions',
      keywords: ['invite', 'send', 'email', 'portal'],
    },
  ]

  const filteredCommands = commands.filter((c) => {
    if (!query) return true
    const q = query.toLowerCase()
    return (
      c.title.toLowerCase().includes(q) ||
      c.subtitle.toLowerCase().includes(q) ||
      c.keywords.some((k) => k.includes(q))
    )
  })

  const searchItems = searchResults.map((r) => ({
    id: r.id,
    title: r.title,
    subtitle: r.subtitle,
    icon: getResultIcon(r.type),
    action: () => {
      if (r.employee_id) {
        onEmployeeClick?.(r.employee_id)
      } else {
        navigate(r.url)
      }
      onClose()
    },
    category: 'search' as const,
  }))

  const allItems: ListItem[] = [
    ...searchItems,
    ...filteredCommands,
  ].map((item, i) => ({ ...item, originalIndex: i }))

  useEffect(() => {
    const timer = setTimeout(async () => {
      if (query.length > 1 && profile?.id) {
        setLoading(true)
        try {
          const results = await globalSearch(query, profile.id, 4)
          setSearchResults(results)
        } catch {
          setSearchResults([])
        } finally {
          setLoading(false)
        }
      } else {
        setSearchResults([])
      }
    }, 150)
    return () => clearTimeout(timer)
  }, [query, profile?.id])

  useEffect(() => {
    if (!isOpen) return
    const handler = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setSelectedIndex((p) => Math.min(p + 1, allItems.length - 1))
          break
        case 'ArrowUp':
          e.preventDefault()
          setSelectedIndex((p) => Math.max(p - 1, 0))
          break
        case 'Enter':
          e.preventDefault()
          allItems[selectedIndex]?.action()
          break
        case 'Escape':
          e.preventDefault()
          onClose()
          break
      }
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [isOpen, selectedIndex, allItems, onClose])

  useEffect(() => {
    if (isOpen) {
      setQuery('')
      setSearchResults([])
      setSelectedIndex(0)
      setTimeout(() => inputRef.current?.focus(), 16)
      trackEvent({ event: 'command_menu_open', userId: profile?.id })
    }
  }, [isOpen, profile?.id])

  useEffect(() => {
    setSelectedIndex(0)
  }, [query])

  // Scroll selected item into view
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-index="${selectedIndex}"]`) as HTMLElement | null
    el?.scrollIntoView({ block: 'nearest' })
  }, [selectedIndex])

  if (!isOpen) return null

  // Group by category
  const grouped: Record<string, ListItem[]> = {}
  for (const item of allItems) {
    if (!grouped[item.category]) grouped[item.category] = []
    grouped[item.category].push(item)
  }
  const categoryOrder = ['search', 'navigation', 'actions']
  const orderedGroups = categoryOrder.filter((c) => grouped[c]?.length)

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[60] bg-black/30 backdrop-blur-[3px]"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        className="fixed inset-0 z-[61] flex items-start justify-center pt-[18vh] px-4 pointer-events-none"
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
      >
        <div
          className="pointer-events-auto w-full max-w-[580px] rounded-2xl overflow-hidden"
          style={{
            background: 'rgba(255,255,255,0.97)',
            border: '1px solid #E5E7EB',
            boxShadow: '0 24px 60px rgba(15,23,42,0.18), 0 4px 16px rgba(15,23,42,0.08)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Search input */}
          <div className="flex items-center gap-3 px-4 py-3.5 border-b border-border">
            <Search className="w-4 h-4 text-[color:var(--color-text-tertiary)] flex-shrink-0" />
            <input
              ref={inputRef}
              type="text"
              placeholder="Search or jump to..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="flex-1 bg-transparent text-[15px] text-foreground placeholder-[#94A3B8] outline-none"
              autoComplete="off"
              spellCheck={false}
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                className="flex-shrink-0 text-[color:var(--color-text-tertiary)] hover:text-muted-foreground transition-colors"
                tabIndex={-1}
              >
                <X className="w-4 h-4" />
              </button>
            )}
            <kbd className="hidden sm:flex items-center justify-center h-5 px-1.5 rounded text-[10px] font-medium text-[color:var(--color-text-tertiary)] bg-panel border border-border flex-shrink-0">
              ESC
            </kbd>
          </div>

          {/* Results */}
          <div ref={listRef} className="max-h-[380px] overflow-y-auto overscroll-contain">
            {loading ? (
              <div className="flex items-center justify-center gap-3 py-10 text-muted-foreground">
                <div className="w-4 h-4 rounded-full border-2 border-border border-t-[#1F2A44] animate-spin" />
                <span className="text-sm">Searching…</span>
              </div>
            ) : allItems.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 gap-2">
                <Search className="w-8 h-8 text-border" />
                <p className="text-sm text-muted-foreground">
                  {query ? `No results for "${query}"` : 'Type to search or browse commands below'}
                </p>
              </div>
            ) : (
              <div className="py-1.5">
                {orderedGroups.map((cat) => (
                  <div key={cat}>
                    <div className="px-4 pt-3 pb-1.5 text-[10.5px] font-semibold uppercase tracking-[0.1em] text-[color:var(--color-text-tertiary)]">
                      {CATEGORY_LABELS[cat]}
                    </div>
                    {grouped[cat].map((item) => {
                      const Icon = item.icon
                      const isSelected = item.originalIndex === selectedIndex
                      return (
                        <button
                          key={item.id}
                          data-index={item.originalIndex}
                          onClick={item.action}
                          onMouseEnter={() => setSelectedIndex(item.originalIndex)}
                          className={[
                            'w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors duration-[150ms]',
                            isSelected
                              ? 'bg-[#F0F4FF]'
                              : 'hover:bg-background',
                          ].join(' ')}
                          aria-selected={isSelected}
                        >
                          <div
                            className={[
                              'flex-shrink-0 flex h-7 w-7 items-center justify-center rounded-lg transition-colors',
                              isSelected
                                ? 'bg-brand/12 text-brand'
                                : 'bg-panel text-muted-foreground',
                            ].join(' ')}
                          >
                            <Icon className="w-3.5 h-3.5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={[
                              'text-[13.5px] font-medium truncate leading-tight',
                              isSelected ? 'text-brand' : 'text-foreground',
                            ].join(' ')}>
                              {item.title}
                            </p>
                            <p className="text-[11.5px] text-muted-foreground truncate leading-tight mt-0.5">
                              {item.subtitle}
                            </p>
                          </div>
                          {isSelected && (
                            <ArrowRight className="w-3.5 h-3.5 text-brand flex-shrink-0" />
                          )}
                        </button>
                      )
                    })}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-4 py-2.5 border-t border-border bg-background">
            <div className="flex items-center gap-3 text-meta text-[color:var(--color-text-tertiary)]">
              <span className="flex items-center gap-1">
                <kbd className="px-1 py-0.5 rounded bg-white border border-border text-[10px]">↑↓</kbd>
                Navigate
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1 py-0.5 rounded bg-white border border-border text-[10px]">↵</kbd>
                Select
              </span>
            </div>
            <span className="text-meta text-[color:var(--color-text-tertiary)]">
              <kbd className="px-1 py-0.5 rounded bg-white border border-border text-[10px]">⌘K</kbd>
              {' '}to open anytime
            </span>
          </div>
        </div>
      </div>
    </>
  )
}
